import React, { useState, useEffect } from 'react';
import { Mail, Send, LogOut, RefreshCw, ArrowLeft, Users, BarChart3, Calendar, Youtube, Image, Rocket, Bell, FileText, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AdminEventsTab from '../components/AdminEventsTab';
import AdminSermonsTab from '../components/AdminSermonsTab';
import AdminGalleryTab from '../components/AdminGalleryTab';
import AdminRelaunchTab from '../components/AdminRelaunchTab';
import AdminNewsletterTab from '../components/AdminNewsletterTab';
import AdminStatsTab from '../components/AdminStatsTab';
import AdminPushNotify from '../components/AdminPushNotify';
import AdminTemplatesManager from '../components/AdminTemplatesManager';
import AdminMessagesTab from '../components/AdminMessagesTab';
import AdminSentTab from '../components/AdminSentTab';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('messages');
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showCompose, setShowCompose] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();

  // Auth helper for all admin API calls
  const authHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  };

  // Sent emails state
  const [sentEmails, setSentEmails] = useState([]);
  const [sentLoading, setSentLoading] = useState(false);
  const [selectedSent, setSelectedSent] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const [composeData, setComposeData] = useState({
    to: '',
    subject: '',
    message: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    if (activeTab === 'sent') {
      fetchSentEmails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/contact/messages`, { headers: authHeaders() });
      const data = await response.json();
      setMessages(data.messages || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSentEmails = async () => {
    try {
      setSentLoading(true);
      const response = await fetch(`${API_URL}/api/admin/sent-emails`, { headers: authHeaders() });
      const data = await response.json();
      setSentEmails(data.emails || []);
    } catch (error) {
      console.error('Error fetching sent emails:', error);
    } finally {
      setSentLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/admin/login');
  };

  const handleExportMessagesCSV = () => {
    const csv = ['Name,E-Mail,Betreff,Datum,Nachricht'];
    messages.forEach(m => {
      const date = m.registered_at ? new Date(m.registered_at).toLocaleDateString('de-DE') : '';
      const msg = (m.message || '').replace(/"/g, '""').replace(/\n/g, ' ');
      csv.push(`"${m.name}","${m.email}","${m.subject}","${date}","${msg}"`);
    });
    const blob = new Blob([csv.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kontaktanfragen-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };


  const handleSendEmail = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/admin/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(composeData)
      });
      if (response.ok) {
        alert('E-Mail erfolgreich gesendet!');
        setShowCompose(false);
        setComposeData({ to: '', subject: '', message: '' });
      } else {
        alert('Fehler beim Senden der E-Mail');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Fehler beim Senden der E-Mail');
    } finally {
      setSending(false);
    }
  };

  const handleReply = (message) => {
    setComposeData({
      to: message.email,
      subject: `Re: ${message.subject}`,
      message: `\n\n--- Original Message ---\nVon: ${message.name} <${message.email}>\nBetreff: ${message.subject}\n\n${message.message}`
    });
    setShowCompose(true);
    setSelectedMessage(null);
  };

  const applyTemplate = (template, contactName, contactEmail) => {
    const name = contactName || selectedMessage?.name || '';
    const email = contactEmail || selectedMessage?.email || '';
    const subject = template.subject.replace(/{name}/g, name).replace(/{email}/g, email);
    const body = template.body.replace(/{name}/g, name).replace(/{email}/g, email);

    setComposeData(prev => ({
      to: prev.to || email,
      subject: subject,
      message: body,
    }));
    setShowCompose(true);
    setSelectedMessage(null);
  };

  if (loading && activeTab === 'messages') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Lade Daten...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg">
        <div className="container mx-auto px-4 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                data-testid="admin-back-btn"
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold">Admin-Dashboard</h1>
                <p className="text-white/80 text-sm">ICG Hannover - Verwaltung</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => { setShowCompose(true); setActiveTab('messages'); }}
                data-testid="admin-new-email-btn"
                className="px-6 py-2 bg-white text-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition-all flex items-center gap-2"
              >
                <Send className="w-4 h-4" />
                Neue E-Mail
              </button>
              <button
                onClick={handleLogout}
                data-testid="admin-logout-btn"
                className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-all flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                Abmelden
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-6">
            <button
              onClick={() => setActiveTab('messages')}
              data-testid="tab-messages"
              className={`px-5 py-2.5 rounded-t-lg font-medium text-sm transition-colors ${
                activeTab === 'messages'
                  ? 'bg-gray-50 text-blue-700'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
            >
              <Mail className="w-4 h-4 inline-block mr-2 -mt-0.5" />
              Posteingang ({messages.length})
            </button>
            <button
              onClick={() => setActiveTab('sent')}
              data-testid="tab-sent"
              className={`px-5 py-2.5 rounded-t-lg font-medium text-sm transition-colors ${
                activeTab === 'sent'
                  ? 'bg-gray-50 text-blue-700'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
            >
              <Send className="w-4 h-4 inline-block mr-2 -mt-0.5" />
              Gesendet ({sentEmails.length})
            </button>
            <button
              onClick={() => setActiveTab('newsletter')}
              data-testid="tab-newsletter"
              className={`px-5 py-2.5 rounded-t-lg font-medium text-sm transition-colors ${
                activeTab === 'newsletter'
                  ? 'bg-gray-50 text-blue-700'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
            >
              <Users className="w-4 h-4 inline-block mr-2 -mt-0.5" />
              Newsletter
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              data-testid="tab-analytics"
              className={`px-5 py-2.5 rounded-t-lg font-medium text-sm transition-colors ${
                activeTab === 'analytics'
                  ? 'bg-gray-50 text-blue-700'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
            >
              <BarChart3 className="w-4 h-4 inline-block mr-2 -mt-0.5" />
              Statistik
            </button>
            <button
              onClick={() => setActiveTab('events')}
              data-testid="tab-events"
              className={`px-5 py-2.5 rounded-t-lg font-medium text-sm transition-colors ${
                activeTab === 'events'
                  ? 'bg-gray-50 text-blue-700'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
            >
              <Calendar className="w-4 h-4 inline-block mr-2 -mt-0.5" />
              Termine
            </button>
            <button
              onClick={() => setActiveTab('sermons')}
              data-testid="tab-sermons"
              className={`px-5 py-2.5 rounded-t-lg font-medium text-sm transition-colors ${
                activeTab === 'sermons'
                  ? 'bg-gray-50 text-blue-700'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
            >
              <Youtube className="w-4 h-4 inline-block mr-2 -mt-0.5" />
              Predigten
            </button>
            <button
              onClick={() => setActiveTab('gallery')}
              data-testid="tab-gallery"
              className={`px-5 py-2.5 rounded-t-lg font-medium text-sm transition-colors ${
                activeTab === 'gallery'
                  ? 'bg-gray-50 text-blue-700'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
            >
              <Image className="w-4 h-4 inline-block mr-2 -mt-0.5" />
              Galerie
            </button>
            <button
              onClick={() => setActiveTab('relaunch')}
              data-testid="tab-relaunch"
              className={`px-5 py-2.5 rounded-t-lg font-medium text-sm transition-colors ${
                activeTab === 'relaunch'
                  ? 'bg-gray-50 text-blue-700'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
            >
              <Rocket className="w-4 h-4 inline-block mr-2 -mt-0.5" />
              Relaunch
            </button>
            <button
              onClick={() => setActiveTab('push')}
              data-testid="tab-push"
              className={`px-5 py-2.5 rounded-t-lg font-medium text-sm transition-colors ${
                activeTab === 'push'
                  ? 'bg-gray-50 text-blue-700'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
            >
              <Bell className="w-4 h-4 inline-block mr-2 -mt-0.5" />
              Push
            </button>
            <button
              onClick={() => setActiveTab('templates')}
              data-testid="tab-templates"
              className={`px-5 py-2.5 rounded-t-lg font-medium text-sm transition-colors ${
                activeTab === 'templates'
                  ? 'bg-gray-50 text-blue-700'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
            >
              <FileText className="w-4 h-4 inline-block mr-2 -mt-0.5" />
              Vorlagen
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Messages Tab */}
        {activeTab === 'messages' && (
          <AdminMessagesTab
            messages={messages}
            selectedMessage={selectedMessage}
            setSelectedMessage={setSelectedMessage}
            showCompose={showCompose}
            setShowCompose={setShowCompose}
            composeData={composeData}
            setComposeData={setComposeData}
            sending={sending}
            handleSendEmail={handleSendEmail}
            handleReply={handleReply}
            applyTemplate={applyTemplate}
            fetchMessages={fetchMessages}
            handleExportMessagesCSV={handleExportMessagesCSV}
          />
        )}

        {/* Newsletter Tab */}
        {activeTab === 'newsletter' && (
          <AdminNewsletterTab />
        )}

        {/* Sent Emails Tab */}
        {activeTab === 'sent' && (
          <AdminSentTab
            sentEmails={sentEmails}
            sentLoading={sentLoading}
            selectedSent={selectedSent}
            setSelectedSent={setSelectedSent}
            fetchSentEmails={fetchSentEmails}
          />
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <AdminStatsTab />
        )}

        {/* Events/Termine Tab */}
        {activeTab === 'events' && (
          <AdminEventsTab />
        )}

        {/* Sermons/Predigten Tab */}
        {activeTab === 'sermons' && (
          <AdminSermonsTab />
        )}

        {/* Gallery Tab */}
        {activeTab === 'gallery' && (
          <AdminGalleryTab />
        )}
        {activeTab === 'relaunch' && (
          <AdminRelaunchTab />
        )}
        {activeTab === 'push' && (
          <AdminPushNotify />
        )}
        {activeTab === 'templates' && (
          <AdminTemplatesManager onSelectTemplate={(t) => applyTemplate(t)} />
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
